import { ArrowRight, BarChart3, ClipboardCheck, FileText, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Footer } from '../components/waitlist/Footer';
import { Navbar } from '../components/waitlist/Navbar';
import { getFleetTrialSignupUrl } from '../config/urls';
import { usePageMeta } from '../hooks/usePageMeta';
import { Analytics } from '../services/analytics';

export function FleetDemoRequest() {
  usePageMeta({
    title: 'Free 3-PCN FleetMode Check - PCNGuard Demo',
    description: 'Request a FleetMode walkthrough or check 3 PCNs free with AI-first PCN control for PCO hire companies.',
    canonical: 'https://pcnguard.co.uk/fleet/demo',
    ogTitle: 'Free 3-PCN FleetMode Check',
    ogDescription: 'Upload a PCN. Let AI extract, check, flag, and prepare the next action faster than manual admin.',
  });

  const trialUrl = getFleetTrialSignupUrl();

  const trackClick = (placement: string) => {
    Analytics.track('fleet_demo_cta_clicked', {
      placement,
      destination: trialUrl,
      audience: 'pco_hire',
    });
  };

  const demoPoints = [
    {
      title: 'AI intake review',
      detail: 'See how FleetMode extracts PCN fields for review from a small sample.',
      icon: FileText,
    },
    {
      title: 'Evidence-gap check',
      detail: 'Review missing hire evidence, driver details, and liability-readiness notes.',
      icon: ClipboardCheck,
    },
    {
      title: 'Deadline and recovery view',
      detail: 'See discount risk, urgent cases, PCN costs, and admin-fee recovery status.',
      icon: BarChart3,
    },
  ] as const;

  return (
    <div className="min-h-screen bg-[#fffaf1] text-[#081b1f] selection:bg-[#d9a45b]/25 font-sans">
      <Navbar />

      <main id="main-content">
        <section className="border-b border-[#d7c3a8] bg-[#f4eadb] px-4 pb-14 pt-24 sm:px-6 sm:pb-18 sm:pt-28">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <div className="inline-flex rounded-md border border-[#dcc8aa] bg-[#fffaf1] px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-[#8a5c22]">
                Free 3-PCN FleetMode Check
              </div>
              <h1 className="mt-5 font-space text-4xl font-black leading-tight tracking-tight text-[#081b1f] sm:text-5xl">
                Test AI-first PCN control before a full rollout.
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-7 text-[#596764] sm:text-lg">
                Upload three real PCNs from your PCO hire workflow. FleetMode helps extract fields, flag evidence gaps, highlight deadline risk, and prepare the next action for your team to review.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <a
                  href={trialUrl}
                  onClick={() => trackClick('hero')}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-[#194d3a] px-6 py-3 font-bold text-white shadow-lg shadow-[#194d3a]/20 transition-all duration-200 hover:bg-[#26654e] active:translate-y-px"
                >
                  Check 3 PCNs free
                  <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href="mailto:info@pcnguard.co.uk?subject=FleetMode%20walkthrough%20request"
                  className="inline-flex items-center justify-center rounded-md border border-[#d6c7b6] bg-white px-6 py-3 font-bold text-[#344347] transition-all duration-200 hover:border-[#194d3a] hover:text-[#194d3a]"
                >
                  Request walkthrough
                </a>
                <Link
                  to="/fleet/features"
                  className="inline-flex items-center justify-center rounded-md border border-[#d6c7b6] bg-white px-6 py-3 font-bold text-[#344347] transition-all duration-200 hover:border-[#194d3a] hover:text-[#194d3a]"
                >
                  Explore features
                </Link>
              </div>
            </div>

            <div className="rounded-lg border border-[#d7c3a8] bg-[#fffaf1] p-5 shadow-[0_24px_80px_rgba(8,27,31,0.14)] sm:p-6">
              <div className="flex items-center justify-between gap-4 border-b border-[#e4d8c8] pb-4">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-wider text-[#8a5c22]">Sample output</div>
                  <h2 className="mt-1 font-space text-2xl font-bold">3-PCN AI Trial Snapshot</h2>
                </div>
                <ShieldCheck className="h-9 w-9 text-[#194d3a]" />
              </div>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {[
                  ['PCNs processed', '3'],
                  ['Admin time saved', 'Estimated 72 min'],
                  ['Deadline risks', '1 urgent'],
                  ['Evidence gaps', '1 missing hire record'],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-md border border-[#e4d8c8] bg-[#f6f0e6] p-4">
                    <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#8a5c22]">{label}</div>
                    <div className="mt-1 font-space text-xl font-bold text-[#081b1f]">{value}</div>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-sm leading-6 text-[#596764]">
                Demo footage will be added when production footage is ready. The current product-led demo is the free 3-PCN FleetMode Check.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-[#fffaf1] px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto grid max-w-7xl gap-4 md:grid-cols-3">
            {demoPoints.map((point) => (
              <article key={point.title} className="rounded-lg border border-[#e4d8c8] bg-[#f6f0e6] p-5 shadow-sm">
                <div className="flex h-11 w-11 items-center justify-center rounded-md border border-[#b5d5c4] bg-[#eaf3ee] text-[#194d3a]">
                  <point.icon className="h-5 w-5" />
                </div>
                <h2 className="mt-4 font-space text-xl font-bold">{point.title}</h2>
                <p className="mt-3 text-sm leading-6 text-[#596764]">{point.detail}</p>
              </article>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default FleetDemoRequest;
